import { Injectable, NgZone } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject, Observable, BehaviorSubject } from 'rxjs';
import { OAuthService } from 'angular-oauth2-oidc';
import { EnvironmentService } from '@abp/ng.core';

@Injectable({ providedIn: 'root' })
export class CommunicationService {
  private hubConnection: signalR.HubConnection | null = null;
  private listeners = new Map<string, Subject<any>>();
  public connectionState$ = new BehaviorSubject<signalR.HubConnectionState>(
    signalR.HubConnectionState.Disconnected
  );
  private biometricLoginCompleteSource = new Subject<void>();
  biometricLoginComplete$ = this.biometricLoginCompleteSource.asObservable();

  constructor(
    private oAuthService: OAuthService,
    private environmentService: EnvironmentService,
    private ngZone: NgZone
  ) {}

  /**
   * Establishes the SignalR connection.
   */
  async connect(): Promise<void> {
    if (this.hubConnection && this.hubConnection.state === 'Connected') {
      return;
    }

    const token = this.oAuthService.getAccessToken();
const apiUrl = this.environmentService.getApiUrl('default');

    const url = `${apiUrl}/signalr-hubs/wine-passport`;

    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: () => token,
      })
      .withAutomaticReconnect()
      .build();

    this.hubConnection.onreconnecting(error => {
      console.warn('SIGNALR | Reconnecting...', error);
      this.ngZone.run(() => {
        this.connectionState$.next(signalR.HubConnectionState.Reconnecting);
      });
    });

    this.hubConnection.onreconnected(connectionId => {
      this.ngZone.run(() => {
        this.connectionState$.next(signalR.HubConnectionState.Connected);
        this.rebindListeners();
      });
    });

    this.hubConnection.onclose(error => {
      console.warn('SIGNALR | Connection closed.', error);
      this.ngZone.run(() => {
        this.connectionState$.next(signalR.HubConnectionState.Disconnected);
        this.hubConnection = null;
      });
      setTimeout(() => this.connect(), 5000);
    });

    while (true) {
      try {
        await this.hubConnection.start();
        this.ngZone.run(() => {
          this.connectionState$.next(signalR.HubConnectionState.Connected);
          this.rebindListeners();
        });
        return;
      } catch (err) {
        console.error('SIGNALR | Failed to connect, retrying in 5s...', err);
        await this.delay(5000);
      }
    }
  }


  /**
   * Reattaches all existing listeners on reconnect.
   */
  private rebindListeners() {
    if (!this.hubConnection) return; // Guarda por si es nulo
    for (const [key, subject] of this.listeners.entries()) {
      this.hubConnection.off(key);
      this.hubConnection.on(key, data => {
        this.ngZone.run(() => {
          subject.next(data);
        });
      });
    }
  }

  /**
   * Registers a listener to a specific SignalR event.
   */
  private async listen<T = any>(eventKey: string): Promise<Observable<T>> {
    let subject = this.listeners.get(eventKey) as Subject<T> | undefined;

    if (!subject) {
      subject = new Subject<T>();
      this.listeners.set(eventKey, subject);
    }

    while (!this.hubConnection || this.hubConnection.state !== 'Connected') {
      await this.delay(300);
    }

    // Prevent multiple bindings
    this.hubConnection.off(eventKey);
    this.hubConnection.on(eventKey, (data: T) => {
      this.ngZone.run(() => {
        subject!.next(data);
      });
    });

    return subject.asObservable();
  }

  /**
   * Delays execution for a given time (ms).
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
  * Registers a listener and runs the callback once the SignalR connection is ready.
  */
  public on<T = any>(eventKey: string, callback: (data: T) => void): void {
    this.listen<T>(eventKey).then(obs => {
      obs.subscribe(callback);
    });
  }

  notifyBiometricLoginComplete() {
    this.biometricLoginCompleteSource.next();
  }
}
